import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useMemo, useEffect, useRef } from 'react';
import useNotificationStore from '../stores/notificationStore';
import { requestNotificationPermission, hasNotificationPermission } from '../utils/browserNotifications';
import {
  NOTIFICATION_QUERY_KEYS,
  showNotificationToast,
} from '../utils/notificationHelpers';
import { useNotificationStream } from './useNotificationStream';
import { LIVE_POLL, LIVE_STALE_MS, livePollInterval } from '../config/liveSync';

export function invalidateNotificationQueries(queryClient) {
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.list });
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
  queryClient.invalidateQueries({ queryKey: ['conversations'] });
}

/**
 * In-app notifications — server is source of truth for isRead.
 */
export const useNotifications = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const showNotification = useNotificationStore((state) => state.showNotification);
  const knownIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  useNotificationStream();

  useEffect(() => {
    if (isAuthenticated && !hasNotificationPermission()) {
      requestNotificationPermission();
    }
  }, [isAuthenticated]);

  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list,
    queryFn: async () => {
      try {
        const response = await apiService.getNotifications();
        if (response.status === 'success' && response.data) {
          return Array.isArray(response.data) ? response.data : [];
        }
        return [];
      } catch {
        return [];
      }
    },
    enabled: !!isAuthenticated,
    staleTime: LIVE_STALE_MS,
    gcTime: 1000 * 60 * 10,
    refetchInterval: () => livePollInterval(LIVE_POLL.NOTIFICATIONS),
    placeholderData: (previous) => previous,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    retry: false,
  });

  const { data: unreadCountData } = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.unreadCount,
    queryFn: async () => {
      try {
        const response = await apiService.getNotificationUnreadCount();
        if (response.status === 'success' && response.data) {
          return response.data.count ?? 0;
        }
      } catch {
        /* fallback */
      }
      return null;
    },
    enabled: !!isAuthenticated,
    staleTime: LIVE_STALE_MS,
    refetchInterval: () => livePollInterval(LIVE_POLL.NOTIFICATIONS_SUMMARY),
    placeholderData: (previous) => previous,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!notifications.length) return;

    if (isFirstLoadRef.current) {
      notifications.forEach((n) => knownIdsRef.current.add(n._id || n.id));
      isFirstLoadRef.current = false;
      return;
    }

    notifications.forEach((n) => {
      const id = n._id || n.id;
      if (!id || knownIdsRef.current.has(id)) return;
      knownIdsRef.current.add(id);
      if (!n.isRead) {
        showNotificationToast(n, showNotification);
      }
    });
  }, [notifications, showNotification]);

  const unreadCount = useMemo(() => {
    if (typeof unreadCountData === 'number') return unreadCountData;
    return notifications.filter((n) => !n.isRead).length;
  }, [unreadCountData, notifications]);

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => apiService.markNotificationAsRead(notificationId),
    onSuccess: (_data, notificationId) => {
      queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.list, (old = []) =>
        old.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n))
      );
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => apiService.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.list, (old = []) =>
        old.map((n) => ({ ...n, isRead: true }))
      );
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId) => apiService.deleteNotification(notificationId),
    onSuccess: (_data, notificationId) => {
      queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.list, (old = []) =>
        old.filter((n) => n._id !== notificationId)
      );
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () => apiService.clearAllNotifications(),
    onSuccess: () => {
      queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.list, []);
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refetch,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    deleteNotification: deleteNotificationMutation.mutateAsync,
    clearAll: clearAllMutation.mutateAsync,
    isMarkingAsRead: markAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
  };
};

export default useNotifications;
